import { Injectable, OnDestroy } from '@angular/core';
import { IResponseMeta, RequestService } from '@df/api/request/request.service';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { Model } from '@df/catalog/model';
import { Product } from '@df/catalog/product/product';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { routeOverride } from '@df/core/meta/route-override.pipe';
import { Events } from '@df/core/service/event';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { TimeoutService } from '@df/ng/timeout.service';
import { Basket } from '@df/session/api/basket/basket';
import { CustomerModel } from '@df/session/api/customer.model';
import last from 'lodash-es/last';
import { Subscription } from 'rxjs';
import { NMinibasket } from '../basket/minibasket.component';
import { LocalSearchResult } from '../search/search-result.decorator';
import { searchResultEvent } from '../search/ui/search-view.component';
import { DfEventService } from '@df/core/service/df-event';

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface ISuccessItemData {
            parent_id?: number;
            rowPrice?: number;
            qty_ordered?: number;
            discount_amount?: number;
        }
    }
}

enum EPromoId {
    Product = 5,
    TopCategory = 3,
    SubCategory = 4,
    Search = 7,
    Cart = 6,
    Success = 100,
    PageDepartment = 2,
    PageStoreFinder = 8,
    PageReferAFriend = 12,
    Homepage = 1,
    Newsletter = 101,
    Account = 942,
    Register = 941
}

export namespace NEpsilon {
    export enum EEpsilonPageType {
        Department = 'department',
        StoreFinder = 'storefinder',
        ReferAFriend = 'refer a friend'
    }
}

@Injectable()
export class EpsilonService implements OnDestroy {
    private subscriptions: Subscription = new Subscription();
    private hashMap = new Map<string, string>();

    private lastTrackedModel?: Model;

    @SessionStorage('epsilonEventId')
    private convertionEventId?: string;

    constructor(
        private app: App,
        private meta: Meta,
        private basket: Basket,
        private customer: CustomerModel,
        private requestService: RequestService,
        private events: Events,
        private timeoutService: TimeoutService,
        private dfEventService: DfEventService
    ) {
        // read param from url
        if (this.app.isPlatformBrowser) {
            let params = new URL(document.location.href).searchParams;
            let eventId = params.get('harmonyconversioneventId');
            if (eventId) {
                this.convertionEventId = eventId;
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    setupTracking() {
        // newsletter
        this.subscriptions.add(
            this.requestService.subscribe((response: IResponseMeta) => {
                const result = response.result;
                if (result?.gtm) {
                    if (
                        (Array.isArray(result.gtm) ? result.gtm : [result.gtm]).find(
                            (data: Gtm.IEvent) => data.event === 'interaction' && (data as Gtm.IEventRegular).eventAction === 'Subscribed'
                        )
                    ) {
                        this.send({ context: 'newsletter' });
                    }
                }
            })
        );

        // minibasket
        this.subscriptions.add(
            this.events.on(NMinibasket.minibasketOpenEvent).subscribe(() => {
                this.send({ context: 'cart' });
            })
        );

        // search
        this.subscriptions.add(
            this.events.on(searchResultEvent).subscribe(el => {
                this.timeoutService.setTimeout(() => {
                    this.send({ searchResult: el });
                }, 2000);
            })
        );

        // success page (tracking case only)
        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SuccessTracking).subscribe((el: Checkout.ISuccessData) => {
                if (el) {
                    this.createSuccessPixel(el);
                    this.send({ checkoutSuccess: el });
                }
            })
        );

        // registration
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === 'nexusCustomerRegister') {
                    // delay cause we need data to be set
                    this.timeoutService.setTimeout(() => {
                        this.send({ context: 'registration' });
                    }, 100);
                }
            })
        );

        // pages
        this.subscriptions.add(
            this.meta.subscribe(() => {
                this.send();
                this.lastTrackedModel = this.getMetaModel();
            })
        );
    }

    private getMetaModel() {
        let model = this.meta?.model;

        if (model?.type === Product.type) {
            model = (model as Product).parent as any;
        }

        return model;
    }

    private async send(options?: {
        context?: 'cart' | 'registration' | 'newsletter';
        checkoutSuccess?: Checkout.ISuccessData;
        searchResult?: LocalSearchResult;
    }): Promise<void> {
        // identify model
        let model = this.getMetaModel();
        const state = this.app.meta.state;

        if (model?.type === Product.type) {
            model = (model as Product).parent as any;
        }

        // identify promo id

        const epsilonPageType = (model?.type === 'page' && (model.data as any)?.additional?.epsilon_page_type) || '';

        const getPromoId = (): EPromoId | undefined => {
            if (options) {
                if (options.searchResult) {
                    return EPromoId.Search;
                }
                if (options.checkoutSuccess) {
                    return EPromoId.Success;
                }
                switch (options.context) {
                    case 'cart':
                        return EPromoId.Cart;
                    case 'registration':
                        return EPromoId.Register;
                    case 'newsletter':
                        return EPromoId.Newsletter;
                }
            }

            if (model?.data) {
                switch (model?.type) {
                    case 'search':
                        return EPromoId.Search;
                    case 'product':
                        return EPromoId.Product;
                    case 'category':
                        const plpLevel = (model?.data as ICategoryDataV3)?.level;
                        if (plpLevel === 2) {
                            return EPromoId.TopCategory;
                        }
                        if (plpLevel >= 3) {
                            return EPromoId.SubCategory;
                        }
                        break;
                    case 'page':
                        switch (epsilonPageType) {
                            case NEpsilon.EEpsilonPageType.Department:
                                return EPromoId.PageDepartment;
                            case NEpsilon.EEpsilonPageType.StoreFinder:
                                return EPromoId.PageStoreFinder;
                            case NEpsilon.EEpsilonPageType.ReferAFriend:
                                return EPromoId.PageReferAFriend;
                            default:
                                if (model.data.url === '/') {
                                    return EPromoId.Homepage;
                                }
                        }
                }
            }

            const url = state?.url;
            if (url) {
                switch (url) {
                    case routeOverride('/basket'):
                        return EPromoId.Cart;
                    // case '/checkout':
                    //     return EPromoId.Checkout;
                }
                if (url.match(/\/account(\/.*)?/)) {
                    return EPromoId.Account;
                }
            }
        };

        const promoId = getPromoId();
        if (!promoId) {
            return;
        }

        // skip double tracking for non modal promos
        if (![EPromoId.Cart, EPromoId.Newsletter].includes(promoId)) {
            if (this.lastTrackedModel && this.lastTrackedModel === model) {
                return;
            }
        }

        // load needed models
        const getNeededModels = () => {
            switch (promoId) {
                case EPromoId.Search:
                    return options?.searchResult?.products?.slice(0, 3).map(el => el.parent);
                case EPromoId.Cart:
                    return this.basket.items.map(el => el.product.parent);
            }
        };

        const models = getNeededModels();
        if (models?.length) {
            await this.app.resources.product.loadAllIds(
                models.map(i => i.id),
                2,
                { safe: true }
            );
        }

        const getEncodedUrl = (): string => {
            const url = this.app.url.toUrl(state?.url || location.pathname);
            return encodeURIComponent(url);
        };

        const getDepartment = (): string | undefined => {
            if (epsilonPageType === NEpsilon.EEpsilonPageType.Department) {
                return this.meta?.model.name;
            }
            if ([EPromoId.TopCategory, EPromoId.SubCategory, EPromoId.Product].includes(promoId)) {
                return state?.breadcrumbs?.[0].label;
            }
        };

        const getCategory = (): string | undefined => {
            if (state?.breadcrumbs && [EPromoId.SubCategory, EPromoId.TopCategory, EPromoId.Product].includes(promoId)) {
                return state.breadcrumbs[1]?.label || '';
            }
        };

        const getSubcategory = (): string | undefined => {
            const breadcrumbs = state?.breadcrumbs;
            if (breadcrumbs) {
                if ([EPromoId.SubCategory, EPromoId.TopCategory].includes(promoId)) {
                    return last(breadcrumbs).label;
                }

                if (promoId === EPromoId.Product && breadcrumbs?.length <= 3) {
                    return breadcrumbs ? breadcrumbs[breadcrumbs.length - 2]?.label : '';
                }
            }
        };

        const getProductId = (): string | undefined => {
            switch (promoId) {
                case EPromoId.Cart:
                    return this.basket.items.map(el => `${this.toProductId(el.product)};${el.qty};${el.data.price}`).join('|');

                case EPromoId.Product:
                    return this.toProductId(model as Product);

                case EPromoId.Search:
                    return options?.searchResult?.products
                        ?.slice(0, 3)
                        .map(el => this.toProductId(el))
                        .join(',');
            }
        };

        const getBrand = (): string | undefined => {
            switch (promoId) {
                case EPromoId.Product:
                    return (model as Product)?.parent?.brand;
                case EPromoId.Cart:
                    return this.basket.items[0]?.product.parent?.brand;
            }
        };

        const getFid = (): number => {
            switch (promoId) {
                case EPromoId.Success:
                    return 5503;
                case EPromoId.Newsletter:
                    return 5505;
            }
            return 5504;
        };

        const domain = this.app.url.storeUrl.replace(this.app.multisite.base, '/');
        const eventData = {
            dtm_cid: '82918',
            dtm_cmagic: 'a8c4c7',
            dtm_fid: getFid(),
            dtm_promo_id: promoId,
            dtm_user_id: this.app.customer.model.data.id,
            dtm_email_hash: '',
            dtmc_transaction_id: options?.checkoutSuccess?.number,
            dtm_conv_val: options?.checkoutSuccess?.totals.grand_total,
            dtm_conv_curr: options?.checkoutSuccess?.currency,
            dtmc_conv_type: promoId === EPromoId.Success && 'delivery',
            dtmc_conv_store_location: promoId === EPromoId.Success && 'online',
            dtm_items: '',
            dtmc_department: getDepartment(),
            dtmc_category: getCategory(),
            dtmc_sub_category: getSubcategory(),
            dtmc_product_id: getProductId(),
            dtmc_brand: getBrand(),
            dtmc_loc: getEncodedUrl(),
            cachebuster: this.getRandomNumber()
        };
        eventData.dtm_email_hash = await this.hashedEmail();
        if (options?.checkoutSuccess) {
            eventData.dtm_items = await this.getCheckoutSuccessItems(options.checkoutSuccess);
        }
        for (const key in eventData) {
            if (eventData[key] === null || eventData[key] === '') {
                eventData[key] = undefined;
            }
        }

        this.requestService.loadScript(`${domain}tag_path/profile/visit/js/1_0?${this.requestService.serialize(eventData)}`, {
            reload: true,
            cleanup: true
        });
    }

    private toProductId(product: Product): string {
        return product.parent.data?.sku;
    }

    private async generateSha256Hash(string: string): Promise<string> {
        if (!this.hashMap.has(string)) {
            const utf8 = new TextEncoder().encode(string);
            const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('');
            this.hashMap.set(string, hashHex);
        }
        return this.hashMap.get(string);
    }

    private async hashedEmail(): Promise<string> {
        const email = this.customer.getSeenEmail();
        return email ? this.generateSha256Hash(email) : '';
    }

    private async getCheckoutSuccessItems(successData: Checkout.ISuccessData): Promise<string> {
        const parentIds = successData?.items?.map(el => this.app.resources.product.get(el.product_id)?.parent?.id).filter(Boolean);
        if (!parentIds?.length) {
            return '';
        }
        await this.app.resources.product.loadAllIds(parentIds, 2, { safe: true });
        return successData
            ?.items!.map(el => {
                const product = this.app.resources.product.get(el.product_id);
                const sku = this.toProductId(product);
                return `${sku};${(el.rowPrice || 0) / (el.qty_ordered || 1)};${el.qty_ordered};${el.discount_amount / el.qty_ordered}`;
            })
            .join('|');
    }

    private getRandomNumber(): number | undefined {
        return Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 1e10);
    }

    private createSuccessPixel(successData: Checkout.ISuccessData) {
        const convertionEventId = this.convertionEventId;
        if (convertionEventId) {
            const img = document.createElement('img') as HTMLImageElement;
            img.height = 1;
            img.width = 1;
            img.src = `https://mail.lemieux.com/C/${convertionEventId}?hEventName=Purchase&hAmount=${successData.total}&hOrderId=${successData.number}`;
            document.body.appendChild(img);
            this.convertionEventId = undefined;
        }
    }
}
